import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  FormHelperText,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Divider,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshButton from '../components/RefreshButton';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import InfoIcon from '@mui/icons-material/Info';
import {
  groupService,
  type CreateGroupRequest,
  type UpdateGroupRequest,
  type GroupDetail,
  type GroupResourceStats,
  type GroupMember,
} from '../services/group.service';
import { userService } from '../services/user.service';
import type { User, Group } from '@/types/user.types';
import { useFieldErrors } from '@/utils/formErrors';

// QA #68: a taken name is the 群組名稱 field's problem.
const GROUP_FIELDS: Record<string, string> = { GROUP_EXISTS: 'name' };
const GROUP_NAME_REQUIRED = '請輸入群組名稱（1–100 個字元）';

// Role display config
const roleConfig: Record<string, { label: string; color: 'error' | 'warning' | 'info' | 'default' }> = {
  admin: { label: '最高管理員', color: 'error' },
  sub_admin: { label: '群組管理員', color: 'warning' },
  operator: { label: '操作員', color: 'info' },
};

// ADR-047: what removing a member carries with them, e.g. "檔案 3 個、File Watcher 1 台".
const describeCarry = (carry: NonNullable<GroupMember['carry']>) =>
  [
    carry.files > 0 ? `檔案 ${carry.files} 個` : '',
    carry.watchers > 0 ? `File Watcher ${carry.watchers} 台` : '',
    carry.schedules > 0 ? `排程 ${carry.schedules} 個` : '',
    carry.backup_jobs > 0 ? `備份工作 ${carry.backup_jobs} 筆` : '',
  ]
    .filter(Boolean)
    .join('、');

export default function Groups() {
  // Groups list state
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateGroupRequest>({ name: '', description: '' });
  const [createLoading, setCreateLoading] = useState(false);
  // createError / editError are for problems that belong to no field; field
  // problems are marked on the input (QA #68).
  const [createError, setCreateError] = useState<string | null>(null);
  const createFields = useFieldErrors();

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [editForm, setEditForm] = useState<UpdateGroupRequest>({ name: '', description: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const editFields = useFieldErrors();

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState<Group | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // QA #49: a group whose files / schedules / watchers / deactivated members
  // still point at it answers 409 GROUP_DELETE_REQUIRES_DESTINATION; the
  // dialog then shows what is left and a destination picker, and retries
  // with move_to.
  const [deleteNeedsDestination, setDeleteNeedsDestination] = useState(false);
  const [deleteDestinationId, setDeleteDestinationId] = useState('');
  const [deleteStats, setDeleteStats] = useState<GroupResourceStats | null>(null);

  // Detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailGroup, setDetailGroup] = useState<GroupDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Add member sub-dialog state
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addMemberLoading, setAddMemberLoading] = useState<string | null>(null);

  // Remove member loading state
  const [removeMemberLoading, setRemoveMemberLoading] = useState<string | null>(null);
  // QA #22: removing a member asks first and says who — the icon used to act
  // on the spot.
  const [memberToRemove, setMemberToRemove] = useState<GroupMember | null>(null);
  // QA #40: leaving the last group needs a destination. The backend says so
  // with 409 LAST_GROUP_REQUIRES_DESTINATION; the dialog then shows a picker
  // (Default Group preselected) and retries with move_to.
  const [needsDestination, setNeedsDestination] = useState(false);
  const [destinationGroupId, setDestinationGroupId] = useState('');

  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await groupService.listGroups();
      setGroups(data);
    } catch (err) {
      console.error('Failed to load groups:', err);
      setError('載入群組列表失敗');
    } finally {
      setLoading(false);
    }
  };

  // --- Create Group ---
  const handleOpenCreate = () => {
    setCreateForm({ name: '', description: '' });
    setCreateError(null);
    createFields.clear();
    setCreateDialogOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateDialogOpen(false);
    setCreateError(null);
    createFields.clear();
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) {
      createFields.setField('name', GROUP_NAME_REQUIRED);
      return;
    }
    try {
      setCreateLoading(true);
      setCreateError(null);
      createFields.clear();
      await groupService.createGroup({
        name: createForm.name.trim(),
        description: createForm.description?.trim() || undefined,
      });
      setSuccessMessage('群組建立成功');
      setCreateDialogOpen(false);
      loadGroups();
    } catch (err) {
      console.error('Failed to create group:', err);
      setCreateError(createFields.apply(err, GROUP_FIELDS, '建立群組失敗，請稍後再試') || null);
    } finally {
      setCreateLoading(false);
    }
  };

  // --- Edit Group ---
  const handleOpenEdit = (group: Group) => {
    setEditingGroup(group);
    setEditForm({ name: group.name, description: group.description || '', is_active: group.is_active ?? true });
    setEditError(null);
    setEditDialogOpen(true);
  };

  // Field hints are dropped on close (handleOpenEdit, which the memoised
  // columns call, keeps capturing nothing but state setters).
  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setEditingGroup(null);
    setEditError(null);
    editFields.clear();
  };

  const handleEdit = async () => {
    if (!editingGroup) return;
    if (!editForm.name?.trim()) {
      editFields.setField('name', GROUP_NAME_REQUIRED);
      return;
    }
    try {
      setEditLoading(true);
      setEditError(null);
      editFields.clear();
      await groupService.updateGroup(editingGroup.id, {
        name: editForm.name?.trim(),
        description: editForm.description?.trim() || undefined,
        is_active: editForm.is_active,
      });
      setSuccessMessage('群組更新成功');
      handleCloseEdit();
      loadGroups();
      // If the detail dialog is open for the same group, reload detail
      if (detailGroup && detailGroup.id === editingGroup.id) {
        loadGroupDetail(editingGroup.id);
      }
    } catch (err) {
      console.error('Failed to update group:', err);
      setEditError(editFields.apply(err, GROUP_FIELDS, '更新群組失敗，請稍後再試') || null);
    } finally {
      setEditLoading(false);
    }
  };

  // --- Delete Group ---
  const handleOpenDelete = (group: Group) => {
    setDeletingGroup(group);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
    setDeletingGroup(null);
    setDeleteNeedsDestination(false);
    setDeleteDestinationId('');
    setDeleteStats(null);
  };

  const deleteDestinationChoices = groups.filter((g) => g.id !== deletingGroup?.id && g.is_active !== false);

  const handleDelete = async () => {
    if (!deletingGroup) return;
    try {
      setDeleteLoading(true);
      const result = await groupService.deleteGroup(
        deletingGroup.id,
        deleteNeedsDestination ? deleteDestinationId : undefined
      );
      const dest = result.moved_to ? groups.find((g) => g.id === result.moved_to)?.name : undefined;
      const movedTotal = Object.values(result.moved ?? {}).reduce((a, b) => a + b, 0);
      setSuccessMessage(
        dest
          ? `群組「${deletingGroup.name}」已刪除，${movedTotal} 筆備份資源已轉移到「${dest}」`
          : `群組「${deletingGroup.name}」已刪除`
      );
      handleCloseDelete();
      loadGroups();
      // Close detail dialog if we just deleted the displayed group
      if (detailGroup && detailGroup.id === deletingGroup.id) {
        setDetailDialogOpen(false);
        setDetailGroup(null);
      }
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      if (code === 'GROUP_DELETE_REQUIRES_DESTINATION') {
        // Keep the dialog open, show what is left and ask where it goes.
        const preferred =
          deleteDestinationChoices.find((g) => g.name === 'Default Group') ?? deleteDestinationChoices[0];
        setDeleteDestinationId(preferred?.id ?? '');
        setDeleteNeedsDestination(true);
        try {
          const detail = await groupService.getGroup(deletingGroup.id);
          setDeleteStats(detail.resource_stats ?? null);
        } catch {
          setDeleteStats(null);
        }
        return;
      }
      console.error('Failed to delete group:', err);
      setError(err instanceof Error ? err.message : '刪除群組失敗');
    } finally {
      setDeleteLoading(false);
    }
  };

  // --- Group Detail ---
  const loadGroupDetail = async (groupId: string) => {
    try {
      setDetailLoading(true);
      const detail = await groupService.getGroup(groupId);
      setDetailGroup(detail);
    } catch (err) {
      console.error('Failed to load group detail:', err);
      setError('載入群組詳情失敗');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenDetail = async (group: Group) => {
    setDetailDialogOpen(true);
    setDetailGroup(null);
    await loadGroupDetail(group.id);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setDetailGroup(null);
  };

  // --- Add Member ---
  const handleOpenAddMember = () => {
    setUserSearchQuery('');
    setSearchResults([]);
    setAddMemberDialogOpen(true);
  };

  const handleCloseAddMember = () => {
    setAddMemberDialogOpen(false);
    setUserSearchQuery('');
    setSearchResults([]);
  };

  const handleSearchUsers = async () => {
    if (!userSearchQuery.trim()) return;
    try {
      setSearchLoading(true);
      const response = await userService.listUsers({ search: userSearchQuery.trim(), page_size: 20 });
      // Filter out users that are already members of this group
      const memberIds = new Set(detailGroup?.members?.map((m) => m.id) || []);
      setSearchResults((response?.items ?? []).filter((u) => !memberIds.has(u.id)));
    } catch (err) {
      console.error('Failed to search users:', err);
      setError('搜尋使用者失敗');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddMember = async (user: User) => {
    if (!detailGroup) return;
    try {
      setAddMemberLoading(user.id);
      await groupService.addMember(detailGroup.id, user.id);
      setSuccessMessage(`已將「${user.username}」加入群組`);
      // Remove from search results
      setSearchResults((prev) => prev.filter((u) => u.id !== user.id));
      // Reload group detail to reflect new member
      await loadGroupDetail(detailGroup.id);
      loadGroups();
    } catch (err) {
      console.error('Failed to add member:', err);
      setError(err instanceof Error ? err.message : '新增成員失敗');
    } finally {
      setAddMemberLoading(null);
    }
  };

  // --- Remove Member ---
  const closeRemoveDialog = () => {
    setMemberToRemove(null);
    setNeedsDestination(false);
    setDestinationGroupId('');
  };

  const destinationChoices = groups.filter((g) => g.id !== detailGroup?.id && g.is_active !== false);

  const handleRemoveMember = async (user: GroupMember, moveTo?: string) => {
    if (!detailGroup) return;
    try {
      setRemoveMemberLoading(user.id);
      const result = await groupService.removeMember(detailGroup.id, user.id, moveTo);
      closeRemoveDialog();
      const dest = moveTo ? groups.find((g) => g.id === moveTo)?.name : undefined;
      const removed = dest
        ? `已將「${user.username}」從群組中移除並移至「${dest}」`
        : `已將「${user.username}」從群組中移除`;
      // ADR-047: say where the member's own files and watchers went.
      const moved = Object.values(result.resources_moved ?? {}).reduce((sum, n) => sum + n, 0);
      const movedTo = groups.find((g) => g.id === result.resources_moved_to)?.name;
      setSuccessMessage(moved > 0 && movedTo ? `${removed}，其 ${moved} 筆資料已一併移到「${movedTo}」` : removed);
      // Reload group detail to reflect removal
      await loadGroupDetail(detailGroup.id);
      loadGroups();
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      if (code === 'LAST_GROUP_REQUIRES_DESTINATION') {
        // Keep the dialog open and ask where the user goes.
        const preferred = destinationChoices.find((g) => g.name === 'Default Group') ?? destinationChoices[0];
        setDestinationGroupId(preferred?.id ?? '');
        setNeedsDestination(true);
        return;
      }
      console.error('Failed to remove member:', err);
      closeRemoveDialog();
      setError(err instanceof Error ? err.message : '移除成員失敗');
    } finally {
      setRemoveMemberLoading(null);
    }
  };

  // --- Formatting helpers ---
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-TW');
  };

  const getRoleChip = (role: string) => {
    const config = roleConfig[role] || { label: role, color: 'default' as const };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const columns = useMemo<GridColDef<Group>[]>(
    () => [
      {
        field: 'name',
        headerName: '群組名稱',
        flex: 1,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'description',
        headerName: '描述',
        flex: 1.5,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        field: 'member_count',
        headerName: '成員數',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip label={params.value ?? 0} size="small" variant="outlined" />
        ),
      },
      {
        // QA #97 follow-up: a disabled group used to be told apart only by
        // opening its edit dialog; the list now says so.
        field: 'is_active',
        headerName: '狀態',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={params.value === false ? '已停用' : '啟用中'}
            color={params.value === false ? 'default' : 'success'}
            size="small"
          />
        ),
      },
      {
        field: 'created_at',
        headerName: '建立時間',
        width: 170,
        valueFormatter: (value: string) => {
          if (!value) return '-';
          return new Date(value).toLocaleString('zh-TW');
        },
      },
      {
        field: 'actions',
        headerName: '操作',
        width: 150,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Tooltip title="詳情">
              <IconButton size="small" onClick={() => handleOpenDetail(params.row)}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="編輯">
              <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="刪除">
              <IconButton size="small" color="error" onClick={() => handleOpenDelete(params.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            群組管理
          </Typography>
          <Typography variant="body2" color="text.secondary">
            管理系統群組與成員
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <RefreshButton onClick={loadGroups} loading={loading} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            新增群組
          </Button>
        </Stack>
      </Stack>

      {/* Alert messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Groups Table */}
      <Paper>
        <DataGrid
          rows={groups}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          autoHeight
          localeText={{ noRowsLabel: '暫無群組資料' }}
          slotProps={{
            pagination: {
              labelRowsPerPage: '每頁筆數：',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} / 共 ${count}`,
            },
          }}
          sx={{ border: 'none', '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }, '& .MuiDataGrid-cell[data-field="actions"]': { justifyContent: 'flex-end' } }}
        />
      </Paper>

      {/* Create Group Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreate} maxWidth="sm" fullWidth>
        <DialogTitle>新增群組</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {createError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="群組名稱"
            fullWidth
            required
            value={createForm.name}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
            disabled={createLoading}
            sx={{ mt: 1 }}
            {...createFields.bind('name')}
          />
          <TextField
            margin="dense"
            label="描述"
            fullWidth
            multiline
            rows={3}
            value={createForm.description}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
            disabled={createLoading}
            {...createFields.bind('description')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreate} disabled={createLoading}>
            取消
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={createLoading}>
            {createLoading ? <CircularProgress size={20} /> : '建立'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>編輯群組</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {editError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="群組名稱"
            fullWidth
            required
            value={editForm.name}
            onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
            disabled={editLoading}
            sx={{ mt: 1 }}
            {...editFields.bind('name')}
          />
          <TextField
            margin="dense"
            label="描述"
            fullWidth
            multiline
            rows={3}
            value={editForm.description}
            onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
            disabled={editLoading}
            {...editFields.bind('description')}
          />
          {/* A-17 / QA #97: disabling a group now locks out members who belong to
              no other active group (admins excepted), hides the group's data from
              everyone else, and signs its members out at once. */}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={editForm.is_active ?? true}
                onChange={(e) => setEditForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                disabled={editLoading}
                color="success"
              />
            }
            label={editForm.is_active === false ? '已停用' : '啟用中'}
          />
          <FormHelperText>
            停用後：只屬於此群組的成員（最高管理員除外）無法登入；同時屬於其他群組的成員看不到此群組的資料；成員目前的登入會立即失效。
          </FormHelperText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={editLoading}>
            取消
          </Button>
          <Button onClick={handleEdit} variant="contained" disabled={editLoading}>
            {editLoading ? <CircularProgress size={20} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Group Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除群組「{deletingGroup?.name}」嗎？此操作無法復原。
          </Typography>
          {(deletingGroup?.member_count ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              此群組尚有 {deletingGroup?.member_count} 位成員，請先在「詳情」中移除所有成員後才能刪除。
            </Alert>
          )}
          {deleteNeedsDestination && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                此群組仍有備份資源或已停用成員的群組資格
                {deleteStats && (
                  <>
                    （檔案 {deleteStats.files_count}、備份工作 {deleteStats.backup_jobs_count}、排程{' '}
                    {deleteStats.schedules_count}、目的地 {deleteStats.destinations_count}、File Watcher{' '}
                    {deleteStats.watchers_count}）
                  </>
                )}
                。刪除前會一併轉移到你指定的群組，該群組的 sub_admin 之後看得到這些資料。
              </Alert>
              <FormControl fullWidth size="small" required>
                <InputLabel>轉移到群組</InputLabel>
                <Select
                  value={deleteDestinationId}
                  label="轉移到群組"
                  onChange={(e) => setDeleteDestinationId(e.target.value)}
                >
                  {deleteDestinationChoices.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deleteLoading}>
            取消
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={
              deleteLoading ||
              (deletingGroup?.member_count ?? 0) > 0 ||
              (deleteNeedsDestination && !deleteDestinationId)
            }
          >
            {deleteLoading ? <CircularProgress size={20} /> : deleteNeedsDestination ? '轉移並刪除' : '刪除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDetail} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">群組詳情 — {detailGroup?.name ?? ''}</Typography>
            <Button
              variant="outlined"
              startIcon={<GroupAddIcon />}
              size="small"
              onClick={handleOpenAddMember}
              disabled={detailLoading || !detailGroup || detailGroup.is_active === false}
            >
              新增成員
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && !detailGroup ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailGroup ? (
            <>
              {/* Group info section */}
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>名稱：</strong>{detailGroup.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>描述：</strong>{detailGroup.description || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>建立時間：</strong>{formatDate(detailGroup.created_at)}
                </Typography>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Members section */}
              <Typography variant="subtitle1" gutterBottom>
                成員列表（{detailGroup.members?.length ?? 0} 人）
              </Typography>

              {detailLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!detailLoading && (!detailGroup.members || detailGroup.members.length === 0) ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  此群組尚無成員
                </Typography>
              ) : (
                <List disablePadding>
                  {detailGroup.members?.map((member) => (
                    <ListItem key={member.id} divider>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={500}>
                              {member.username}
                            </Typography>
                            {getRoleChip(member.role)}
                          </Stack>
                        }
                        secondary={member.email}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="移除成員">
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => setMemberToRemove(member)}
                            disabled={removeMemberLoading === member.id}
                          >
                            {removeMemberLoading === member.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <PersonRemoveIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Sub-Dialog */}
      <Dialog open={addMemberDialogOpen} onClose={handleCloseAddMember} maxWidth="sm" fullWidth>
        <DialogTitle>新增成員</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            搜尋使用者並加入群組「{detailGroup?.name}」
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              size="small"
              label="搜尋使用者（帳號或 Email）"
              fullWidth
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchUsers();
                }
              }}
              disabled={searchLoading}
            />
            <Button
              variant="contained"
              onClick={handleSearchUsers}
              disabled={searchLoading || !userSearchQuery.trim()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {searchLoading ? <CircularProgress size={20} /> : '搜尋'}
            </Button>
          </Stack>

          {searchResults.length > 0 ? (
            <List disablePadding>
              {searchResults.map((user) => (
                <ListItem key={user.id} divider>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={500}>
                          {user.username}
                        </Typography>
                        {getRoleChip(user.role)}
                      </Stack>
                    }
                    secondary={user.email}
                  />
                  <ListItemSecondaryAction>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        addMemberLoading === user.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <AddIcon />
                        )
                      }
                      onClick={() => handleAddMember(user)}
                      disabled={addMemberLoading === user.id}
                    >
                      加入
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : userSearchQuery.trim() && !searchLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              沒有符合的使用者，或所有搜尋結果已是群組成員
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMember}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* Remove Member confirmation (QA #22) */}
      <Dialog open={memberToRemove !== null} onClose={closeRemoveDialog} maxWidth="xs" fullWidth>
        <DialogTitle>移除成員</DialogTitle>
        <DialogContent>
          <Typography>
            確定要將「{memberToRemove?.username}」從群組「{detailGroup?.name}」移除嗎？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            移除後該使用者將看不到此群組共享的備份目的地，帳號本身不會被停用。
          </Typography>
          {memberToRemove?.carry && describeCarry(memberToRemove.carry) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              「{memberToRemove.username}」在此群組的{describeCarry(memberToRemove.carry)}，會一併移到
              {`「${
                (needsDestination
                  ? groups.find((g) => g.id === destinationGroupId)?.name
                  : memberToRemove.carry.to_group_name) ?? '去向群組'
              }」`}
              。
            </Alert>
          )}
          {needsDestination && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                這是「{memberToRemove?.username}」的最後一個群組。使用者必須屬於一個群組，請指定移出後的去向。
              </Alert>
              <FormControl fullWidth size="small" required>
                <InputLabel>去向群組</InputLabel>
                <Select
                  value={destinationGroupId}
                  label="去向群組"
                  onChange={(e) => setDestinationGroupId(e.target.value)}
                >
                  {destinationChoices.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveDialog}>取消</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => memberToRemove && handleRemoveMember(memberToRemove, needsDestination ? destinationGroupId : undefined)}
            disabled={!memberToRemove || removeMemberLoading !== null || (needsDestination && !destinationGroupId)}
          >
            {needsDestination ? '移除並移至去向群組' : '確認移除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
